// src/fetch.ts
import {
  blocksGet,
  delegatesGet,
  operationsGetByHash,
  operationsGetDelegations,
  operationsGetOriginations,
  operationsGetTransactions,
  tokensGetTokenBalances,
  tokensGetTokenTransfers
} from "@tzkt/sdk-api";
import * as tzktApi from "@tzkt/sdk-api";
import axios from "axios";
import { identity, pickBy, sortBy } from "lodash";

// src/withRateLimit.ts
import Semaphore from "@chriscdn/promise-semaphore";
import { CustomError } from "@umami/utils";
import promiseRetry from "promise-retry";
var tzktRateLimiter = new Semaphore(10);
var withRateLimit = (fn) => tzktRateLimiter.acquire().then(() => promiseRetry((retry) => fn().catch(retry), { retries: 3, minTimeout: 100 })).catch((error) => {
  if ("status" in error && "data" in error) {
    throw new CustomError(
      `Fetching data from tzkt failed with: ${error.status}, ${error.data}`
    );
  }
  throw error;
}).finally(() => tzktRateLimiter.release());

// src/fetch.ts
var getAccounts = async (pkhs, network) => withRateLimit(
  () => fetch(
    `${network.tzktApiUrl}/v1/accounts?${new URLSearchParams({
      ["address.in"]: pkhs.join(","),
      ["select.fields"]: "address,balance,delegate,stakedBalance,unstakedBalance,rollupBonds,smartRollupBonds"
    })}`
  ).then((res) => res.json())
);
var getRecvForSafeSendTx = async (contractAddress, network, hash) => withRateLimit(
  async () => axios.get(`${network.tzktApiUrl}/v1/contracts/${contractAddress}/bigmaps/sending/keys/${hash}`, {
    headers: { "Content-Type": "application/json" }
  }).then((res) => res.data.value ?? null)
);
var getTokenBalances = async (address, network) => withRateLimit(
  () => tokensGetTokenBalances(
    {
      account: { eq: address },
      balance: { gt: "0" },
      limit: 1e4
    },
    {
      baseUrl: network.tzktApiUrl
    }
  )
);
var getDelegations = async (address, network, options) => withRateLimit(
  () => operationsGetDelegations(
    { sender: { eq: address }, ...options },
    {
      baseUrl: network.tzktApiUrl
    }
  )
);
var getTransactions = async (address, network, options) => withRateLimit(
  () => operationsGetTransactions(
    {
      anyof: { fields: ["sender", "target", "initiator"], eq: address },
      ...options
    },
    {
      baseUrl: network.tzktApiUrl
    }
  )
);
var getOriginations = async (address, network, options) => withRateLimit(
  () => operationsGetOriginations(
    { sender: { eq: address }, ...options },
    {
      baseUrl: network.tzktApiUrl
    }
  )
);
var getStakingOperations = async (address, network, options) => withRateLimit(async () => {
  const params = pickBy(
    {
      limit: options.limit,
      "sender.eq": address,
      "sort.asc": options.sort.asc,
      "sort.desc": options.sort.desc,
      "offset.cr": options.offset?.cr
      // TODO: add select.fields
    },
    identity
  );
  const response = await fetch(
    `${network.tzktApiUrl}/v1/operations/staking?${new URLSearchParams(params)}`
  );
  const data = await response.json();
  return data.map(
    (operation) => ({ ...operation, type: operation.action })
  );
});
var getCombinedOperations = async (address, network, options) => {
  const limit = options?.limit || 100;
  const sort = options?.sort ?? "desc";
  const tzktRequestOptions = {
    limit,
    offset: options?.lastId ? { cr: options.lastId } : void 0,
    sort: { [sort]: "id" }
  };
  const operations = await Promise.all([
    getTransactions(address, network, tzktRequestOptions),
    getDelegations(address, network, tzktRequestOptions),
    getOriginations(address, network, tzktRequestOptions),
    getTokenTransfers(address, network, tzktRequestOptions),
    getStakingOperations(address, network, tzktRequestOptions)
  ]);
  return sortBy(
    operations.flat(),
    (operation) => sort === "asc" ? operation.id : -operation.id
  ).slice(0, limit);
};
var getRelatedTokenTransfers = async (transactionIds, network) => {
  if (transactionIds.length === 0) {
    return [];
  }
  return withRateLimit(
    () => tokensGetTokenTransfers(
      // tzkt doesn't work with the `in` operator correctly
      // the only way is to have just one element in it and join it with a comma manually
      { transactionId: { in: [transactionIds.join(",")] } },
      { baseUrl: network.tzktApiUrl }
    )
  );
};
var getTokenTransfers = async (address, network, options) => withRateLimit(async () => {
  const tokenTransfers = await tokensGetTokenTransfers(
    {
      anyof: {
        fields: ["from", "to"],
        eq: address
      },
      ...options
    },
    {
      baseUrl: network.tzktApiUrl
    }
  );
  return tokenTransfers.map((transfer) => ({
    ...transfer,
    type: "token_transfer"
  }));
});
var getTezosPriceInUSD = () => withRateLimit(() => tzktApi.quotesGetLast().then((quote) => quote.usd));
var getLatestBlock = async (network) => withRateLimit(async () => {
  const [block] = await blocksGet(
    {
      limit: 1,
      sort: { desc: "id" }
    },
    {
      baseUrl: network.tzktApiUrl
    }
  );
  return { level: block.level, cycle: block.cycle };
});
var getBakers = async (network) => withRateLimit(
  () => delegatesGet(
    {
      sort: { desc: "votingPower" },
      active: { eq: true },
      limit: 1e4,
      select: { fields: ["address,alias,votingPower"] }
    },
    {
      baseUrl: network.tzktApiUrl
    }
  ).then(
    (delegates) => delegates.map((delegate) => ({
      name: delegate.alias || "Unknown baker",
      address: delegate.address,
      stakingBalance: delegate.votingPower ?? 0
    }))
  )
);
var getPendingUnstakeRequests = async (network, addresses) => withRateLimit(async () => {
  const requests = await fetch(
    `${network.tzktApiUrl}/v1/staking/unstake_requests?${new URLSearchParams({
      limit: "10000",
      "staker.in": addresses.join(","),
      type: "unstake",
      "status.ne": "finalized",
      "select.fields": "cycle,actualAmount,staker,status"
    })}`
  ).then((res) => res.json());
  return requests.map((request) => ({
    cycle: request.cycle,
    amount: request.actualAmount,
    staker: request.staker,
    status: request.status
  }));
});
var getOperationsByHash = (hash, network) => withRateLimit(() => operationsGetByHash(hash, {}, { baseUrl: network.tzktApiUrl }));

// src/schemas.ts
import { z } from "zod";
var AddressSchema = z.object({ address: z.string(), alias: z.string().optional() });
var FA12TokenSchema = z.object({
  standard: z.string().regex(/^fa1\.2$/i),
  contract: AddressSchema
});
var FA2TokenSchema = z.object({
  standard: z.string().regex(/^fa2$/i),
  tokenId: z.string(),
  contract: AddressSchema
});
var NFTSchema = z.object({
  id: z.number(),
  standard: z.string().regex(/^fa2$/i),
  tokenId: z.string(),
  contract: AddressSchema,
  totalSupply: z.string().optional(),
  metadata: z.object({
    displayUri: z.string(),
    decimals: z.void().or(z.string().regex(/^0$/))
  })
});

// src/testUtils.ts
var mockTzktTezTransfer = (sender, recipient, amount, timestamp = "2020-05-24T13:12:18Z", hash = "onqKcUZLbN9LdoJsPeRtDrHqYibm4osdtQJmGGQ4GAg3SjQUMT3", level = 10) => ({
  id: 12345,
  type: "transaction",
  sender: { address: sender },
  target: { address: recipient },
  timestamp,
  amount,
  hash,
  level,
  transactionId: 54321
});
export {
  AddressSchema,
  FA12TokenSchema,
  FA2TokenSchema,
  NFTSchema,
  getAccounts,
  getBakers,
  getCombinedOperations,
  getDelegations,
  getLatestBlock,
  getOperationsByHash,
  getOriginations,
  getPendingUnstakeRequests,
  getRecvForSafeSendTx,
  getRelatedTokenTransfers,
  getStakingOperations,
  getTezosPriceInUSD,
  getTokenBalances,
  getTokenTransfers,
  getTransactions,
  mockTzktTezTransfer,
  withRateLimit
};
